import { AfterViewInit, Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";
import { GetMagicCardRequest, GetMagicCardResponse, GetUserResourceVisibilityResponse, UserProtectedResource, ViaCollection } from "@apiserver-rest/ponder-data-client-base";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { Title } from "@angular/platform-browser";
import { debounceTime, Subscription, take } from "rxjs";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { addSegmentToList, initializeUserSegmentArray } from "@ponder-features/header/navbar/navbar.component";
import { FilteredPreviewItems } from "@ponder-shared/tools/filter/filtered-preview-items";
import { PreviewItemFilters } from "@ponder-shared/tools/filter/preview-item-filters";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { AuthService } from "@ponder-core/auth/auth.service";
import { MagicCollectionInfoComponent } from "./magic-collection-info.component";
import { VirtualGridComponent } from "@ponder-shared/virtual/virtual-grid.component";
import { VirtualGridOptions } from "@ponder-shared/virtual/virtual-grid-options";
import { SmoothSlider } from "@ponder-shared/utils/smoothSlider";
import { AuthSession } from "@ponder-core/auth/auth-session";

@Component({
    selector: "magic-collection",
    imports: [LoadingComponent, MagicCollectionInfoComponent, VirtualGridComponent],
    templateUrl: "./magic-collection.component.html"
})
export class MagicCollectionComponent implements AfterViewInit {
    @Input() public username!: string;
        
    @ViewChild("collectionContentsContainer") private collectionContentsContainerRef!: ElementRef<HTMLElement>;
    @ViewChild("collectionInfoContainer") private collectionInfoContainerRef!: ElementRef<HTMLElement>;

    private smoothSlider?: SmoothSlider;

    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;
    public isVisible?: boolean = undefined;

    public previewItems?: FilteredPreviewItems;
    public gridOptions: VirtualGridOptions = {};

    private filterChangeSub?: Subscription;
    
    public constructor(
            private ponderDataClient: PonderDataClient, 
            private pageTitle: Title,
            private events: EventBroker,
            private auth: AuthService) {
        this.events.ui.navbarUpdateEvent.emit();
        this.filterChangeSub = this.events.ui.previewFilterChangeEvent
            .pipe(debounceTime(100))
            .subscribe({
                next: (filter: PreviewItemFilters) => {
                    this.onFilterUpdated(filter);
                }
        });
    }

    public ngOnInit() {
        this.events.ui.enteredCollectionEvent.emit();
        
        this.auth.getSessionAsync().then((authSession?: AuthSession) => {
            if (authSession?.username && (authSession.username == this.username)) {
                this.gridOptions.isOwnCollection = true;
            } else {
                // this is temporary (lol) until I figure out how to handle viewing of another collection.
                // when requesting a collection it gets the collected counts for that collection, not for the current user.
                // so it will highlight everything as collected. need to handle distinguishing. until then ...
                this.gridOptions.isOwnCollection = true;
            }
        });
    }

    public ngOnDestroy() {
        this.filterChangeSub?.unsubscribe();
    }

    public ngAfterViewInit(): void {
        this.smoothSlider = new SmoothSlider(this.collectionInfoContainerRef.nativeElement, this.collectionContentsContainerRef.nativeElement, 119, 250);
        this.pageTitle.setTitle(`Ponder > Magic Collection > ${this.username}`);
        this.sendNavbarUpdate();

        this.fetchCollectionVisibility();
    }

    private fetchCollectionVisibility(): void {
        this.ponderDataClient
            .userResourceVisibility(this.username, UserProtectedResource.Collection)
            .pipe(take(1))
            .subscribe({
                next: (response: GetUserResourceVisibilityResponse) => {
                    if (response.isVisible) {
                        this.isVisible = true;
                        this.fetchCollectionContents();
                    }
                }
            })
            .add(() => {
                if (this.isVisible == undefined) {
                    this.isVisible = false;
                    this.isLoading = false;
                    this.loadingSuccess = true;
                }
            });
    }

    private fetchCollectionContents(): void {
        const getRequest = new GetMagicCardRequest({
            preview: true,
            collectionProps: new ViaCollection({
                username: this.username
            })
        });

        this.ponderDataClient.magicCard(getRequest)
            .pipe(take(1))
            .subscribe({
                next: (response: GetMagicCardResponse) => {
                    if (response.success && response.previews) {
                        this.loadingSuccess = true;
                        
                        this.previewItems = new FilteredPreviewItems(response.previews, this.events);
                        this.previewItems.populate(undefined);
                        
                        this.events.ui.toggleSortFilterBarEvent.emit(true);
                        this.events.ui.previewItemsFetchedEvent.emit(this.previewItems);
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private sendNavbarUpdate(): void {
        var segments = initializeUserSegmentArray(this.username);
        addSegmentToList(segments, "Collection", "Collection", `/user/${this.username}/collection`);
        addSegmentToList(segments, "Magic: The Gathering", "Magic");
        
        this.events.ui.navbarUpdateEvent.emit(segments);
    }
    
    private onFilterUpdated(itemFilter: PreviewItemFilters) {
        this.previewItems?.populate(itemFilter);
        window.scroll(0, 0);
    }
        
    @HostListener("window:scroll", ["$event"])
    @HostListener('window:resize', ['$event'])
    private onWindowChange(): void {
        this.smoothSlider?.onWindowChange();
    }
}